import React, { useEffect, useState, useMemo } from 'react'

import {
    Modal,
    Paper,
    Slide,
    Theme,
    Typography,
    IconButton,
    useTheme,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Button,
} from '@material-ui/core'
import { toast } from 'react-toastify'
import { Close as CloseIcon, Event as EventIcon } from '@material-ui/icons'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'

import {
    BaseWorkorder,
    EarlyAccessRequest,
    EarlyAccessRequestStatus,
    ListVendor,
    Service,
    WorkorderStatus,
    WorkSpaceUser,
} from '../../../models'
import { WorkorderResponse } from '../../../store'
import { Container, MessageForm, Selector } from '../../../components'
import { WorkorderRow } from '../../../components/WorkorderLib/WorkorderRow/WorkorderRow'
import { useWorkorderApi } from '../../../contexts'
import { useEarlyAccessRequest } from '../../../hooks/useEarlyAccessRequest'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    open: boolean
    workspaceUser?: WorkSpaceUser
    serviceList: Service[]
    vendorList: ListVendor[]
    onClose: () => void
}

export const EarlyAccessRequestsListModal = (props: Props) => {
    const { open, onClose } = props

    const theme = useTheme()

    const { transitionWorkorder } = useWorkorderApi()

    const {
        earlyAccessRequestList,
        earlyAccessLoadingState,
        getEarlyAccessRequestList,
        updateEarlyAccessRequest,
        bulkUpdateEarlyAccessRequests,
        filteredEarlyAccessRequests,
        earlyAccessRequestFilterState,
        setEarlyAccessRequestFilterState,
    } = useEarlyAccessRequest()

    const [openMessageChannel, setOpenMessageChannel] = useState<{
        channel: number
        channelType: 'workorder' | 'unit_notes'
    }>({ channel: -1, channelType: 'workorder' })

    const [selectedRequests, setSelectedRequests] = useState<Set<number>>(
        new Set(),
    )

    useEffect(() => {
        if (open) {
            getEarlyAccessRequestList()
            setSelectedRequests(new Set()) // Reset selection when modal opens
        }
    }, [open])

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const allIds =
                filteredEarlyAccessRequests?.map((req) => req.id) || []
            setSelectedRequests(new Set(allIds))
        } else {
            setSelectedRequests(new Set())
        }
    }

    const handleSelectRequest = (requestId: number, checked: boolean) => {
        const newSelected = new Set(selectedRequests)
        if (checked) {
            newSelected.add(requestId)
        } else {
            newSelected.delete(requestId)
        }
        setSelectedRequests(newSelected)
    }

    const handleBulkApprove = async () => {
        if (selectedRequests.size === 0) return

        try {
            await bulkUpdateEarlyAccessRequests({
                early_access_request_ids: Array.from(selectedRequests),
                status: EarlyAccessRequestStatus.APPROVED,
            })
            setSelectedRequests(new Set())
            toast.success(
                `Successfully approved ${selectedRequests.size} request(s)`,
            )
        } catch (error) {
            console.error('Error bulk approving requests:', error)
            toast.error('Error approving requests')
        }
    }

    const handleBulkDeny = async () => {
        if (selectedRequests.size === 0) return

        try {
            await bulkUpdateEarlyAccessRequests({
                early_access_request_ids: Array.from(selectedRequests),
                status: EarlyAccessRequestStatus.DENIED,
            })
            setSelectedRequests(new Set())
            toast.success(
                `Successfully denied ${selectedRequests.size} request(s)`,
            )
        } catch (error) {
            console.error('Error bulk denying requests:', error)
            toast.error('Error denying requests')
        }
    }

    const isAllSelected =
        filteredEarlyAccessRequests &&
        filteredEarlyAccessRequests.length > 0 &&
        filteredEarlyAccessRequests.every((req) => selectedRequests.has(req.id))

    const isIndeterminate =
        selectedRequests.size > 0 &&
        selectedRequests.size < (filteredEarlyAccessRequests?.length || 0)

    return (
        <Modal
            open={open}
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
            }}
        >
            <>
                <Slide direction="up" in={open}>
                    <Paper
                        elevation={24}
                        style={{
                            width: '90vw',
                            maxWidth: '1200px',
                            height: '85vh',
                            maxHeight: '800px',
                            borderRadius: '12px',
                            overflow: 'hidden',
                        }}
                    >
                        <EarlyAccessRequestsList
                            {...props}
                            openMessageChannel={openMessageChannel}
                            theme={theme}
                            filteredEarlyAccessRequests={
                                filteredEarlyAccessRequests ?? []
                            }
                            loading={
                                earlyAccessLoadingState.getEarlyAccessRequestList
                            }
                            selectedRequests={selectedRequests}
                            isAllSelected={isAllSelected}
                            isIndeterminate={isIndeterminate}
                            filterState={earlyAccessRequestFilterState}
                            earlyAccessRequests={earlyAccessRequestList}
                            setOpenMessageChannel={setOpenMessageChannel}
                            transitionWorkorder={transitionWorkorder}
                            updateEarlyAccessRequest={updateEarlyAccessRequest}
                            onSelectRequest={handleSelectRequest}
                            onSelectAll={handleSelectAll}
                            onBulkApprove={handleBulkApprove}
                            onBulkDeny={handleBulkDeny}
                            onFilterChange={setEarlyAccessRequestFilterState}
                        />
                    </Paper>
                </Slide>
            </>
        </Modal>
    )
}

interface EarlyAccessRequestsListProps extends Props {
    openMessageChannel: {
        channel: number
        channelType: 'workorder' | 'unit_notes'
    }
    theme: Theme
    earlyAccessRequests: EarlyAccessRequest[]
    loading: boolean
    setOpenMessageChannel: React.Dispatch<
        React.SetStateAction<{
            channel: number
            channelType: 'workorder' | 'unit_notes'
        }>
    >
    transitionWorkorder: (
        workorder: BaseWorkorder,
        status: WorkorderStatus,
    ) => Promise<WorkorderResponse>
    updateEarlyAccessRequest: (
        id: number,
        status: EarlyAccessRequestStatus,
    ) => Promise<EarlyAccessRequest>
    selectedRequests: Set<number>
    onSelectRequest: (requestId: number, checked: boolean) => void
    onSelectAll: (checked: boolean) => void
    isAllSelected: boolean
    isIndeterminate: boolean
    onBulkApprove: () => void
    onBulkDeny: () => void
    filterState: {
        vendorId: number | null
        serviceId: number | null
    }
    onFilterChange: (filters: {
        vendorId: number | null
        serviceId: number | null
    }) => void
    filteredEarlyAccessRequests: EarlyAccessRequest[]
}

export const EarlyAccessRequestsList = (
    props: EarlyAccessRequestsListProps,
) => {
    const {
        earlyAccessRequests,
        workspaceUser,
        openMessageChannel,
        theme,
        filteredEarlyAccessRequests,
        loading,
        selectedRequests,
        isAllSelected,
        isIndeterminate,
        filterState,
        serviceList,
        vendorList,
        setOpenMessageChannel,
        onClose,
        transitionWorkorder,
        updateEarlyAccessRequest,
        onSelectRequest,
        onSelectAll,
        onBulkApprove,
        onBulkDeny,
        onFilterChange,
    } = props

    const materialTheme = useTheme()

    const totalCount = earlyAccessRequests.length
    const selectedCount = selectedRequests.size

    const serviceOptions = useMemo(() => {
        const filteredServiceList = serviceList.filter((srv) =>
            earlyAccessRequests.some(
                (earlyAccess) => earlyAccess.workorder.service_id === srv.id,
            ),
        )

        return filteredServiceList.map((srv) => {
            return { ...srv, key: srv.id, label: srv.name, value: srv.id }
        })
    }, [serviceList, earlyAccessRequests])

    const vendorOptions = useMemo(() => {
        const filteredVendorList = vendorList.filter((vnd) =>
            earlyAccessRequests.some(
                (earlyAccess) => earlyAccess.workorder.vendor_id === vnd.id,
            ),
        )

        console.log('filteredVendorList', filteredVendorList)

        return filteredVendorList.map((vnd) => {
            return { ...vnd, key: vnd.id, label: vnd.name, value: vnd.id }
        })
    }, [vendorList, earlyAccessRequests])

    console.log('vendorOptions', vendorOptions)

    return (
        <Container direction="column" style={{ height: '100%' }}>
            {/* Header */}
            <Container
                style={{
                    padding: materialTheme.spacing(1, 2, 1, 2),
                    borderBottom: `1px solid ${materialTheme.palette.divider}`,
                    backgroundColor: materialTheme.palette.background.paper,
                    alignItems: 'center',
                }}
            >
                <Container style={{ alignItems: 'center', flex: 1 }}>
                    <EventIcon
                        style={{
                            fontSize: 28,
                            color: materialTheme.palette.primary.main,
                            marginRight: materialTheme.spacing(2),
                        }}
                    />
                    <Container direction="column">
                        <Typography variant="h5" style={{ fontWeight: 600 }}>
                            Early Access Requests
                        </Typography>
                    </Container>
                </Container>

                {/* Stats */}
                <Container
                    style={{
                        alignItems: 'center',
                        marginRight: materialTheme.spacing(2),
                    }}
                >
                    <Typography variant="body2" color="textSecondary">
                        {selectedCount > 0
                            ? `${selectedCount}/${totalCount} selected`
                            : `${totalCount} early access requests`}
                    </Typography>
                </Container>

                {/* Close Button */}
                <IconButton
                    onClick={onClose}
                    style={{
                        backgroundColor: materialTheme.palette.grey[100],
                        marginLeft: materialTheme.spacing(1),
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Container>

            {/* Content */}
            <Container style={{ flex: 1, overflow: 'hidden' }}>
                <Container
                    direction="column"
                    style={{
                        flex: 1,
                        overflow: 'hidden',
                        padding: materialTheme.spacing(2, 3, 3, 3),
                    }}
                >
                    {loading ? (
                        <Container
                            direction="column"
                            style={{
                                flex: 1,
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: materialTheme.palette.text.secondary,
                            }}
                        >
                            <CircularProgress
                                size={60}
                                style={{
                                    marginBottom: materialTheme.spacing(2),
                                    color: materialTheme.palette.primary.main,
                                }}
                            />
                            <Typography variant="h6" gutterBottom>
                                Loading early access requests...
                            </Typography>
                            <Typography variant="body2">
                                Fetching early access requests
                            </Typography>
                        </Container>
                    ) : earlyAccessRequests.length === 0 ? (
                        <Container
                            direction="column"
                            style={{
                                flex: 1,
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: materialTheme.palette.text.secondary,
                            }}
                        >
                            <EventIcon
                                style={{
                                    fontSize: 64,
                                    marginBottom: materialTheme.spacing(2),
                                }}
                            />
                            <Typography variant="h6" gutterBottom>
                                No early access requests
                            </Typography>
                            <Typography variant="body2">
                                {filterState.vendorId || filterState.serviceId
                                    ? 'No requests match the current filters'
                                    : 'Early access requests will appear here'}
                            </Typography>
                        </Container>
                    ) : (
                        <Container
                            direction="column"
                            style={{
                                flex: 1,
                                overflowY: 'auto',
                                gap: materialTheme.spacing(2),
                            }}
                        >
                            {/* Select All Checkbox and Filters */}
                            <Container
                                style={{
                                    padding: materialTheme.spacing(1, 2),
                                    borderBottom: `1px solid ${materialTheme.palette.divider}`,
                                    marginBottom: materialTheme.spacing(1),
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isAllSelected}
                                            indeterminate={isIndeterminate}
                                            onChange={(e) =>
                                                onSelectAll(e.target.checked)
                                            }
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Typography
                                            variant="subtitle1"
                                            style={{ fontWeight: 600 }}
                                        >
                                            Select All (
                                            {filteredEarlyAccessRequests.length}
                                            )
                                        </Typography>
                                    }
                                />

                                <Container
                                    style={{
                                        alignItems: 'center',
                                        gap: materialTheme.spacing(2),
                                    }}
                                >
                                    <Selector
                                        label="Vendor"
                                        currentValue={
                                            filterState.vendorId || -1
                                        }
                                        onChange={(event) =>
                                            onFilterChange({
                                                ...filterState,
                                                vendorId:
                                                    (event.target
                                                        .value as number) ||
                                                    null,
                                            })
                                        }
                                        data={vendorOptions}
                                        getDisplayString={(item: any) =>
                                            item.name || ''
                                        }
                                        noSelectionLabel="All Vendors"
                                        customStyle={{
                                            formControl: { minWidth: 150 },
                                        }}
                                    />

                                    <Selector
                                        label="Service"
                                        currentValue={
                                            filterState.serviceId || -1
                                        }
                                        onChange={(event) =>
                                            onFilterChange({
                                                ...filterState,
                                                serviceId:
                                                    (event.target
                                                        .value as number) ||
                                                    null,
                                            })
                                        }
                                        data={serviceOptions}
                                        getDisplayString={(item: any) =>
                                            item.name || ''
                                        }
                                        noSelectionLabel="All Services"
                                        customStyle={{
                                            formControl: { minWidth: 150 },
                                        }}
                                    />
                                </Container>
                            </Container>

                            {/* Early Access Requests List */}
                            {filteredEarlyAccessRequests.map(
                                (earlyAccessRequest) => {
                                    const wo = earlyAccessRequest.workorder
                                    return (
                                        <Container
                                            key={`EARLY_ACCESS_${earlyAccessRequest.id}`}
                                            style={{
                                                border: `1px solid ${materialTheme.palette.divider}`,
                                                borderRadius:
                                                    materialTheme.shape
                                                        .borderRadius,
                                                padding: materialTheme.spacing(
                                                    1,
                                                ),
                                                backgroundColor: selectedRequests.has(
                                                    earlyAccessRequest.id,
                                                )
                                                    ? materialTheme.palette
                                                          .action.selected
                                                    : materialTheme.palette
                                                          .background.paper,
                                            }}
                                        >
                                            {/* Checkbox and WorkorderRow */}
                                            <Container
                                                style={{
                                                    alignItems: 'center',
                                                    width: '100%',
                                                }}
                                            >
                                                <Checkbox
                                                    checked={selectedRequests.has(
                                                        earlyAccessRequest.id,
                                                    )}
                                                    onChange={(e) =>
                                                        onSelectRequest(
                                                            earlyAccessRequest.id,
                                                            e.target.checked,
                                                        )
                                                    }
                                                    disabled={
                                                        earlyAccessRequest.status ===
                                                            EarlyAccessRequestStatus.APPROVED ||
                                                        earlyAccessRequest.status ===
                                                            EarlyAccessRequestStatus.DENIED
                                                    }
                                                    color="primary"
                                                    style={{
                                                        marginRight: materialTheme.spacing(
                                                            1,
                                                        ),
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Container
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <WorkorderRow
                                                        workorder={wo}
                                                        user={workspaceUser}
                                                        canSeeUnitNotes
                                                        isSelectedWorkorder={
                                                            wo.channel ===
                                                            openMessageChannel.channel
                                                        }
                                                        openMessageDrawer={() => {
                                                            const cId =
                                                                openMessageChannel.channel ===
                                                                wo.channel
                                                                    ? -1
                                                                    : wo.channel
                                                            setOpenMessageChannel(
                                                                {
                                                                    channel: cId,
                                                                    channelType:
                                                                        'workorder',
                                                                },
                                                            )
                                                        }}
                                                        openUnitNotes={() => {
                                                            if (
                                                                !wo.unit_channel_id
                                                            ) {
                                                                return
                                                            }
                                                            setOpenMessageChannel(
                                                                {
                                                                    channel:
                                                                        wo.unit_channel_id,
                                                                    channelType:
                                                                        'unit_notes',
                                                                },
                                                            )
                                                        }}
                                                        transitionWorkorder={(
                                                            wo,
                                                            status,
                                                        ) => {
                                                            return transitionWorkorder(
                                                                wo,
                                                                status,
                                                            ).then((wo) => {})
                                                        }}
                                                        onClickWorkorder={() => {}}
                                                        updateWorkorderState={() => {}}
                                                        style={{
                                                            width: '100%',
                                                            borderRadius: '8px',
                                                            boxShadow:
                                                                materialTheme
                                                                    .shadows[1],
                                                            transition:
                                                                'all 0.2s ease-in-out',
                                                        }}
                                                        earlyAccessRequest={
                                                            earlyAccessRequest
                                                        }
                                                        onHandleEarlyAccessRequest={(
                                                            approve,
                                                        ) => {
                                                            updateEarlyAccessRequest(
                                                                earlyAccessRequest.id,
                                                                approve
                                                                    ? EarlyAccessRequestStatus.APPROVED
                                                                    : EarlyAccessRequestStatus.DENIED,
                                                            )
                                                                .then(() => {
                                                                    toast.success(
                                                                        `Successfully ${
                                                                            approve
                                                                                ? 'approved'
                                                                                : 'denied'
                                                                        } early access request`,
                                                                    )
                                                                })
                                                                .catch(
                                                                    (error) => {
                                                                        console.error(
                                                                            'Error updating early access request:',
                                                                            error,
                                                                        )
                                                                        toast.error(
                                                                            `Error ${
                                                                                approve
                                                                                    ? 'approving'
                                                                                    : 'denying'
                                                                            } early access request`,
                                                                        )
                                                                    },
                                                                )
                                                        }}
                                                    />
                                                </Container>
                                            </Container>
                                        </Container>
                                    )
                                },
                            )}
                        </Container>
                    )}

                    {/* Bulk Actions - Fixed at Bottom of Content */}
                    {selectedCount > 0 && (
                        <Container
                            style={{
                                padding: materialTheme.spacing(2, 3),
                                borderTop: `1px solid ${materialTheme.palette.divider}`,
                                backgroundColor:
                                    materialTheme.palette.primary.light,
                                alignItems: 'center',
                                gap: materialTheme.spacing(2),
                                marginTop: materialTheme.spacing(1),
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                style={{
                                    fontWeight: 600,
                                    color: materialTheme.palette.primary.main,
                                }}
                            >
                                {selectedCount} request(s) selected
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={onBulkApprove}
                                style={{
                                    backgroundColor:
                                        materialTheme.palette.primary.main,
                                    color:
                                        materialTheme.palette.primary
                                            .contrastText,
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    borderRadius:
                                        materialTheme.shape.borderRadius,
                                    boxShadow: materialTheme.shadows[2],
                                }}
                            >
                                Bulk Approve ({selectedCount})
                            </Button>

                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={onBulkDeny}
                                style={{
                                    backgroundColor:
                                        materialTheme.palette.secondary.main,
                                    color:
                                        materialTheme.palette.secondary
                                            .contrastText,
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    borderRadius:
                                        materialTheme.shape.borderRadius,
                                    boxShadow: materialTheme.shadows[2],
                                }}
                            >
                                Bulk Deny ({selectedCount})
                            </Button>
                        </Container>
                    )}
                </Container>

                {openMessageChannel.channel !== -1 && (
                    <Container
                        style={{
                            width: 400,
                            borderLeft: `1px solid ${theme.palette.grey[700]}`,
                            flexDirection: 'column',
                        }}
                    >
                        <Container
                            style={{
                                padding: theme.spacing(1),
                                alignItems: 'center',
                            }}
                        >
                            <IconButton
                                onClick={() => {
                                    setOpenMessageChannel({
                                        channel: -1,
                                        channelType: 'workorder',
                                    })
                                }}
                            >
                                <ChevronRightIcon />
                            </IconButton>

                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h5),
                                    fontWeight: 700,
                                    marginLeft: theme.spacing(1),
                                }}
                            >
                                {openMessageChannel.channelType === 'workorder'
                                    ? 'Messages'
                                    : 'Unit Notes'}
                            </span>
                        </Container>

                        <MessageForm
                            theme={theme}
                            channelId={openMessageChannel.channel}
                        />
                    </Container>
                )}
            </Container>
        </Container>
    )
}
